import { BeLocationStep, CzLocationStep } from 'components/Onboarding/LocationStep';
import {
  BelgiumOnboardingValues,
  CzechOnboardingValues,
  OnboardingValues,
  TerminalAcquirer,
} from 'components/Onboarding/types';
import { isEqual, merge, omit, pick } from 'lodash';
import { BelgiumLocationConfig, CountryCode, LocationConfig } from 'types/config';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/localStorageManager';

export const getInitialValues = (
  isoCode: CountryCode,
  activity: string = '',
  products: string[] = [],
): OnboardingValues => {
  switch (isoCode) {
    case 'cz':
      return {
        country: 'cz',
        ...CzLocationStep.initial(),
        activities: [{ activity, subActivity: '', terActivity: '' }],
      } as CzechOnboardingValues;
    case 'be':
      return {
        country: 'be',
        ...BeLocationStep.initial(),
        products,
      } as BelgiumOnboardingValues;
    default:
      throw 'No initial values exist for this country';
  }
};

const getKey = (contractId?: string) => `onboarding-${contractId}`;

export const discardOnboarding = (contractId: string) => {
  removeLocalStorage(getKey(contractId));
};

export const storeOnboarding = (contractId: string, changes: OnboardingValues) => {
  setLocalStorage(getKey(contractId), changes);
};

/**
 * Calculates initial onboarding values
 *
 * @param isoCode Current country
 * @param config Location country config
 * @param contractId Contract identifier used to retrieve data from local storage
 * @param activity Activity to be pre-set on generating initial values
 * @param products Products to be pre-set on generating initial values
 * @param terminalAcquirers Possible list of 4C terminal acquirers
 */
export const getInitialOnboardingValues = (
  isoCode: CountryCode,
  config: LocationConfig,
  contractId: string,
  activity: string = '',
  products: string[] = [],
  terminalAcquirers: TerminalAcquirer[] = [],
) => {
  const initialValues = getInitialValues(isoCode, activity, products);
  const storedValues: OnboardingValues = getLocalStorage(getKey(contractId), {});

  const pickedValues = pick(storedValues, Object.keys(initialValues));
  const resultValues = {} as OnboardingValues;
  merge(resultValues, initialValues, pickedValues);

  switch (resultValues.country) {
    case 'be': {
      // try to find selected acquirer in the list of acquirers, if not present, set empty value or
      // NOACQUIRER in the case 4C is disabled
      const findAcquirer = (acquirer: string) =>
        (config as BelgiumLocationConfig).terminals4C || false
          ? terminalAcquirers.find((a) => a.value === acquirer)?.value || ''
          : 'NOACQUIRER';

      return {
        ...resultValues,
        terminalAcquirer: findAcquirer(resultValues.terminalAcquirer),
        terminalAcquirerOnline: findAcquirer(resultValues.terminalAcquirerOnline),
      } as BelgiumOnboardingValues;
    }
    default:
      return resultValues;
  }
};

export const hasModifiedOnboarding = (contractId: string, isoCode: CountryCode) => {
  try {
    const initialValues = getInitialValues(isoCode, '', []);
    const storedValues = getLocalStorage(getKey(contractId), {});
    const maybeValues = merge({}, initialValues, pick(storedValues, Object.keys(initialValues)));

    const toOmit = ['type', 'products', 'activities'];
    const maybeValuesOmitted = omit(maybeValues, toOmit);
    const initValuesOmitted = omit(initialValues, toOmit);

    return !isEqual(initValuesOmitted, maybeValuesOmitted);
  } catch(_e) {
    return false;
  }
};
